import React, { useEffect, useState } from "react";
import axiosInstance from '../networking/axiosconfig';
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import { Calendar, Clock, Mail, FileText, CheckCircle2, XCircle } from 'lucide-react';
import Swal from "sweetalert2";
import Spinner from "../component/Spinner";
import ProfileAvatar from '../component/ProfileAvtar';
import Navbar from './Navbar';



const CurrentLeaveList = () => {
    const [device, setDevice] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [currentLeaveId, setCurrentLeaveId] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: "date", direction: "desc" });

    const sortedDevice = [...device].sort((a, b) => {
        let dateA = new Date(a.date).getTime();
        let dateB = new Date(b.date).getTime();
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
    });

    const handleSort = () => {
        setSortConfig((prev) => ({
            key: "date",
            direction: prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const apiUrl = `/api/leave/current/leave`;

    const groupLeavesByGroupId = (leaves) => {
        const groupedLeaves = {};

        leaves.forEach((leave) => {
            const groupId = leave.leave_group_id;

            if (!groupedLeaves[groupId]) {
                groupedLeaves[groupId] = {
                    ...leave,
                    dates: [new Date(leave.date)],
                };
            } else {
                groupedLeaves[groupId].dates.push(new Date(leave.date));
            }
        });

        return Object.values(groupedLeaves).map((leave) => ({
            ...leave,
            start_date: leave.dates.length > 0 ? leave.dates[0] : null,
            end_date: leave.dates.length > 1 ? leave.dates[leave.dates.length - 1] : leave.dates[0],
            formatted_date: `${leave.dates[0].toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            })} - ${leave.dates[leave.dates.length - 1].toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            })}`,
        }));
    };

    const fetchLeaveData = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(apiUrl);
            if (res.data && Array.isArray(res.data.data)) {
                const groupedLeaves = groupLeavesByGroupId(res.data.data);
                setDevice(groupedLeaves);
            } else {
                // "No leave found" comes back without a data array: show the empty state
                setDevice([]);
            }
        } catch (err) {
            console.error("API Error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeaveData();
    }, []);

    const handleApprove = async (leaveId) => {
        if (!leaveId) return console.error("Leave ID is undefined");
        const userId = localStorage.getItem("userId");

        const result = await Swal.fire({
            title: "Are you sure?",
            text: "Do you want to approve this leave request?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, Approve",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#10b981",
            cancelButtonColor: "#6b7280",
            reverseButtons: true,
        });

        if (result.isConfirmed) {
            try {
                await axiosInstance.post(`/api/leave/leave-request-approved/${leaveId}`, {
                    leave_approved_by_user_id: userId,
                });

                Swal.fire({
                    icon: "success",
                    title: "Leave Approved",
                    text: "The leave request has been approved successfully.",
                    timer: 1500,
                    showConfirmButton: false,
                });

                fetchLeaveData();
            } catch (error) {
                console.error("Error approving leave:", error);
                Swal.fire({
                    icon: "error",
                    title: "Approval Failed",
                    text: "Failed to approve the leave request. Please try again.",
                });
            }
        }
    };

    const openRejectModal = (leaveId) => {
        setCurrentLeaveId(leaveId);
        setShowModal(true);
    };

    const handleReject = async () => {
        if (!currentLeaveId || !rejectReason.trim()) {
            return Swal.fire({
                icon: "warning",
                title: "Missing Data",
                text: "Please provide a rejection reason.",
            });
        }

        const userId = localStorage.getItem("userId");

        try {
            await axiosInstance.post(`/api/leave/leave-request-rejected/${currentLeaveId}`, {
                rej_leave_by_user_id: userId,
                reason: rejectReason,
            });

            setShowModal(false);
            setRejectReason("");
            fetchLeaveData();

            Swal.fire({
                icon: "success",
                title: "Leave Rejected",
                text: "The leave request has been rejected successfully.",
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error("Error rejecting leave:", error);
            Swal.fire({
                icon: "error",
                title: "Rejection Failed",
                text: "Failed to reject the leave request. Please try again.",
            });
        }
    };

    const getStatusBadge = (status) => {
        if (status === 1) {
            return (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                    <CheckCircle2 size={14} />
                    Approved
                </span>
            );
        } else if (status === 2) {
            return (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                    <XCircle size={14} />
                    Rejected
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                <Clock size={14} />
                Pending
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            <Navbar />
            <div className="p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className=" rounded-2xl shadow-2xl p-6 mb-6 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 text-white ">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl font-bold  flex items-center gap-3">
                                    <Calendar className="" size={36} />
                                    Current Leave Requests
                                </h1>
                                <p className=" mt-2">Manage and review pending leave requests</p>
                            </div>
                            <div className="bg-blue-100 px-4 py-2 rounded-lg">
                                <p className="text-sm text-blue-700 font-semibold">
                                    Total Requests: {device.length}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Leave Cards */}
                    {loading ? (
                        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                            <Spinner size={40} color="#3b82f6" loading={loading} />
                            <p className="text-gray-600 mt-4">Loading leave requests...</p>
                        </div>
                    ) : sortedDevice.length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                            <FileText className="mx-auto text-gray-400 mb-4" size={48} />
                            <p className="text-gray-600 text-lg">No leave requests found</p>
                            <p className="text-gray-500 text-sm mt-2">All leave requests have been processed</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-6">
                            {sortedDevice.map((element, index) => (
                                <div
                                    key={element.leave_id_PK}
                                    className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow"
                                >
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                        {/* Employee Info */}
                                        <div className="flex items-center gap-4 flex-1">
                                            <ProfileAvatar
                                                className="h-16 w-16"
                                                url={element.user_profile_img}
                                                name={`${element.first_name || "User"} ${element.last_name || ""}`}
                                            />
                                            <div className="flex-1">
                                                <h3 className="text-lg font-bold text-gray-800">
                                                    {element.first_name || "N/A"} {element.last_name || ""}
                                                </h3>
                                                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                                                    <div className="flex items-center gap-1">
                                                        <Mail size={16} />
                                                        <span>{element.email}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Calendar size={16} />
                                                        <span>{element.formatted_date || "N/A"}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <FileText size={16} />
                                                        <span className="font-medium">{element.leave_name}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Status and Actions */}
                                        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                                            <div>
                                                {getStatusBadge(element.status)}
                                            </div>
                                            {element.status !== 1 && element.status !== 2 && (
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleApprove(element.leave_group_id)}
                                                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold shadow-md hover:shadow-lg"
                                                        title="Approve Leave"
                                                    >
                                                        <CheckCircleIcon className="w-5 h-5" />
                                                        Approve
                                                    </button>
                                                    <button
                                                        onClick={() => openRejectModal(element.leave_group_id)}
                                                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold shadow-md hover:shadow-lg"
                                                        title="Reject Leave"
                                                    >
                                                        <XCircleIcon className="w-5 h-5" />
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Approved/Rejected By */}
                                    {(element.status === 1 || element.status === 2) && (
                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                            <p className="text-sm text-gray-600">
                                                {element.status === 1 ? "Approved" : "Rejected"} by:{" "}
                                                <span className="font-semibold text-gray-800">
                                                    {element.rejected_by_first_name || "N/A"}{" "}
                                                    {element.rejected_by_last_name || ""}
                                                </span>
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Reject Modal */}
            {showModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-2xl font-bold text-gray-800">Reject Leave Request</h2>
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    setRejectReason("");
                                }}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                            >
                                <XCircleIcon className="w-6 h-6" />
                            </button>
                        </div>
                        <p className="text-gray-600 mb-4">Please provide a reason for rejecting this leave request:</p>
                        <textarea
                            className="w-full border-2 border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all resize-none"
                            rows="4"
                            placeholder="Enter rejection reason..."
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                        />
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    setRejectReason("");
                                }}
                                className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReject}
                                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold shadow-md hover:shadow-lg"
                            >
                                Reject Leave
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CurrentLeaveList;
